import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, DropdownV2, SearchBox, SimpleInput } from '@makyoapp/makyo-ui'

import { useGlobalVariableValues } from '@/core/app-builder/global-variables'
import { useGlobalVariables } from '@/hooks/useGlobalVariables'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { appIconKeys } from '@/shared/icons'
import { useFormEnter } from '@/shared/makyo-hooks'

interface IGlobalVariablesDropdownProps {
  value: string
  onChange: (value: string) => void
  editable?: boolean
}

const FORM_ID = 'new-global-variable-form'

/**
 * Global Variables Dropdown for App Builder
 */
export default function GlobalVariablesDropdown({ value, onChange, editable = false }: IGlobalVariablesDropdownProps) {
  const { globalVariables, addGlobalVariable, removeGlobalVariable } = useGlobalVariables()
  const { getVariableValue } = useGlobalVariableValues()

  const [show, setShow] = useState(false)
  const [search, setSearch] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [removeVarName, setRemoveVarName] = useState('')
  const [newVarName, setNewVarName] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)

  const handleToggle = (open: boolean) => {
    setShow(open)
  }

  const options = useMemo(() => {
    if (search.length > 0) {
      return globalVariables.filter((variable) => variable.toLowerCase().includes(search.toLowerCase()))
    }
    return globalVariables
  }, [search, globalVariables])

  const handleOpenEdit = () => {
    setIsEditing(true)

    setTimeout(() => {
      inputRef.current?.focus()
    }, 100)
  }

  const handleCloseEdit = () => {
    setIsEditing(false)
    setNewVarName('')
  }

  const handleAddNew = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault()

    if (isSaving) return

    if (!newVarName?.trim().length) return

    try {
      setIsSaving(true)

      await addGlobalVariable(newVarName)

      setIsEditing(false)
      setNewVarName('')
    } catch (error) {
      // do nothing
      toast.error(error instanceof Error ? error.message : 'Failed to add global variable')
    } finally {
      setIsSaving(false)
    }
  }

  useFormEnter(FORM_ID, () => {
    handleAddNew()
  })

  const handleRemove = async (varName: string) => {
    if (removeVarName) return

    try {
      setRemoveVarName(varName)
      await removeGlobalVariable(varName)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove global variable')
    } finally {
      setRemoveVarName('')
    }
  }

  useEffect(() => {
    if (!show) {
      setSearch('')
      setIsEditing(false)
    }
  }, [show])

  const getValue = (varName: string) => {
    const value = getVariableValue(varName)
    return value
  }

  const currentSelectedValue = getValue(value)

  return (
    <DropdownV2 open={show} onOpenChange={handleToggle}>
      <DropdownV2.Trigger asChild>
        <div className='h-fit flex-1 min-w-0 flex items-center justify-between gap-2 px-2 py-2.5 rounded-sm bg-zinc-100 dark:bg-slate-900 hover:bg-zinc-200 dark:hover:bg-slate-700'>
          <div className='flex-1 min-w-0'>
            {value ? (
              <div className='flex items-center gap-2'>
                <MaterialIcon icon={appIconKeys.storage} size={16} className='shrink-0' />
                <span className='shrink-0'>{value}</span>
                {currentSelectedValue && (
                  <span className='text-light truncate'>&nbsp;{`{ Value: ${currentSelectedValue} }`}</span>
                )}
              </div>
            ) : (
              <span className='text-light'>Select Global Variable</span>
            )}
          </div>

          <MaterialIcon icon='keyboard_arrow_down' size={16} className='shrink-0' />
        </div>
      </DropdownV2.Trigger>

      <DropdownV2.Content
        className='z-302 w-[464px] text-m-m'
        side='bottom'
        align='start'
        onInteractOutside={(e) => {
          if (isSaving) {
            e.preventDefault()
          }
        }}
      >
        <div className='p-3 border-b bdr'>
          <span className='text-m-m flex items-center gap-2 font-medium text-light'>
            <MaterialIcon icon={appIconKeys.globalVariable} size={16} />
            <Trans>Global Variables</Trans>
          </span>
        </div>

        {/* search */}
        {globalVariables.length > SHOW_SEARCH_LIMIT && (
          <div className='border-b bdr font-normal'>
            <SearchBox
              value={search}
              onSearch={(search) => {
                setSearch(search)
              }}
              onClearSearch={() => setSearch('')}
              noBorder
            />
          </div>
        )}

        <div className='font-normal min-h-[120px] max-h-[300px] overflow-y-scroll'>
          {options.length === 0 ? (
            <>
              {search.length > 0 ? (
                <div className='p-2 flex items-center h-full text-light'>
                  <Trans>No global variables found</Trans>
                </div>
              ) : (
                <div className='p-2 flex items-center h-full text-light'>
                  <Trans>No global variables found. Add a new one to continue.</Trans>
                </div>
              )}
            </>
          ) : (
            options.map((variableName, index) => {
              const isSelected = value === variableName
              const isRemoving = removeVarName === variableName

              const currentValue = getValue(variableName)

              return (
                <div
                  key={`${variableName}-${index}`}
                  className={clsx(
                    'w-full cursor-pointer flex items-center justify-between gap-2 px-4 h-11',
                    isSelected ? 'bg-zinc-100 dark:bg-slate-700' : 'hover:bg-zinc-100 dark:hover:bg-slate-700/50'
                  )}
                  onClick={() => {
                    onChange(variableName)
                  }}
                >
                  <div className='flex items-center gap-2'>
                    <MaterialIcon icon={appIconKeys.storage} size={16} className='text-light shrink-0' />
                    <span className='shrink-0'>{variableName}</span>
                    {currentValue && <span className='text-light truncate'>{`{ Value: ${currentValue} }`}</span>}
                  </div>

                  {editable && (
                    <button
                      title='Remove'
                      className={clsx(
                        'cursor-pointer group w-8 h-8 flex-cc rounded-sm hover:bg-zinc-100 dark:hover:bg-slate-900/50 -mr-1',
                        isRemoving && 'opacity-50'
                      )}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemove(variableName)
                      }}
                      disabled={isRemoving}
                    >
                      {isRemoving ? (
                        <span className='loading spin'></span>
                      ) : (
                        <MaterialIcon icon='delete' size={14} className='group-hover:red' />
                      )}
                    </button>
                  )}
                </div>
              )
            })
          )}
        </div>

        {editable && (
          <div className='border-t bdr p-3'>
            {isEditing ? (
              <form onSubmit={handleAddNew} id={FORM_ID} className='flex items-center gap-2'>
                <SimpleInput
                  ref={inputRef}
                  value={newVarName}
                  onChange={(e) => setNewVarName(e.target.value)}
                  placeholder='Enter new variable name'
                />

                <div className='h-fit flex items-center gap-1'>
                  <button
                    className={clsx(
                      'group cursor-pointer w-8 h-8 flex-cc rounded-sm hover:bg-zinc-100 dark:hover:bg-slate-900/80',
                      isSaving && 'opacity-50'
                    )}
                    onClick={handleCloseEdit}
                    disabled={isSaving}
                  >
                    <MaterialIcon icon='close' size={16} className='group-hover:red' />
                  </button>

                  <button
                    className={clsx(
                      'group cursor-pointer w-8 h-8 flex-cc rounded-sm hover:bg-zinc-100 dark:hover:bg-slate-900/80',
                      isSaving && 'opacity-50'
                    )}
                    onClick={() => {
                      handleAddNew()
                    }}
                  >
                    {isSaving ? (
                      <span className='loading spin'></span>
                    ) : (
                      <MaterialIcon icon='done' size={16} className='group-hover:text-brand-500' />
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <Button
                variant={ButtonVariant.OutlinedGray}
                className='w-full'
                onClick={handleOpenEdit}
                icon={<MaterialIcon icon='add' size={16} />}
              >
                <Trans>Add New Variable</Trans>
              </Button>
            )}
          </div>
        )}
      </DropdownV2.Content>
    </DropdownV2>
  )
}
